/**
 * Authentication APIs for Electron
 * Handles auth tokens, user email, and auth-related event listening
 */
import { ElectronAuthIpcEvent } from '@littlebird/auth-client';

import { ElectronAPI } from '../base/ElectronAPI';
import { type ElectronListenerMethod } from '../base/types';

export class AuthAPI extends ElectronAPI {
  /**
   * Set authentication data - handled by Electron main process
   * Stores auth data securely and calls ContextKit to set token and URL
   */
  async setAuthData(params: {
    accessToken: string;
    refreshToken: string;
    clientId: string;
    auth0Domain: string;
    apiUrl: string;
  }): Promise<void> {
    // Store auth data in electron main process
    await this.authApi.setAuthData(params);
  }

  /**
   * Get authentication data - handled by Electron main process
   */
  async getAuthData() {
    return this.authApi.getAuthData();
  }

  /**
   * Clear authentication data - handled by Electron main process
   */
  async clearAuthData() {
    return this.authApi.clearAuthData();
  }

  /**
   * Check if user is authenticated - handled by Electron main process
   */
  async isAuthenticated() {
    return this.authApi.isAuthenticated();
  }

  /**
   * Get access token - handled by Electron main process
   */
  async getAccessToken() {
    return this.authApi.getAccessToken();
  }

  /**
   * Get refresh token - handled by Electron main process
   */
  async getRefreshToken() {
    return this.authApi.getRefreshToken();
  }

  /**
   * Request auth token - handled by Electron main process
   */
  async requestToken(source?: string, options?: { force?: boolean }) {
    return this.authApi.requestToken(source, options);
  }

  /**
   * Start a login attempt - handled by Electron main process
   */
  async startLoginAttempt(): Promise<{ id: string }> {
    return this.authApi.startLoginAttempt();
  }

  /**
   * Listen for auth tokens expiration
   * Uses direct ipcRenderer since these events come from main process, not ContextKit
   */
  listenAuthTokensExpire: ElectronListenerMethod<any> = (
    callback,
    _onError,
    onUnlisten,
    timeout
  ) => {
    if (!this.isElectronAvailable) {
      return () => {};
    }

    const listener = (_event: any, payload: any) => {
      callback(payload);
    };

    const unsubscribe = this.ipcRenderer.on(
      ElectronAuthIpcEvent.TokensDidExpire,
      listener
    );

    const cleanup = () => {
      unsubscribe();
      onUnlisten?.();
    };

    // Set timeout if provided
    if (timeout !== undefined && timeout > 0) {
      setTimeout(() => {
        cleanup();
      }, timeout);
    }

    return cleanup;
  };

  /**
   * Listen for auth token refresh
   * Uses direct ipcRenderer since these events come from main process, not ContextKit
   */
  listenAuthTokenRefresh: ElectronListenerMethod<any> = (
    callback,
    _onError,
    onUnlisten,
    timeout
  ) => {
    if (!this.isElectronAvailable) {
      return () => {};
    }

    const listener = (_event: any, payload: any) => {
      callback(payload);
    };

    const unsubscribe = this.ipcRenderer.on(
      ElectronAuthIpcEvent.TokensDidRefresh,
      listener
    );

    const cleanup = () => {
      unsubscribe();
      onUnlisten?.();
    };

    // Set timeout if provided
    if (timeout !== undefined && timeout > 0) {
      setTimeout(() => {
        cleanup();
      }, timeout);
    }

    return cleanup;
  };
}
